import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Instructor {
  name: string;
  title: string;
  company: string;
  image: string;
  imageAlt: string;
  bio: string;
  experience: string;
  linkedin: string;
}

interface InstructorProfilesProps {
  instructors: Instructor[];
}

export default function InstructorProfiles({ instructors }: InstructorProfilesProps) {
  return (
    <section className="py-16 bg-card">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-headline font-bold text-foreground mb-4">
            Learn from Industry Experts
          </h2>
          <p className="text-lg text-muted-foreground font-body max-w-3xl mx-auto">
            Our instructors bring real-world experience from leading tech companies to guide your learning journey.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {instructors.map((instructor, index) => (
            <div
              key={index}
              className="bg-surface rounded-xl border border-border overflow-hidden hover:shadow-sky-md transition-all duration-300"
            >
              <div className="relative h-64 overflow-hidden">
                <AppImage
                  src={instructor.image}
                  alt={instructor.imageAlt}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 to-transparent"></div>
              </div>

              <div className="p-6">
                <h3 className="text-xl font-headline font-bold text-foreground mb-1">
                  {instructor.name}
                </h3>
                <p className="text-sm text-primary font-semibold mb-1">
                  {instructor.title}
                </p>
                <p className="text-sm text-muted-foreground font-medium mb-4">
                  {instructor.company}
                </p>

                <p className="text-sm text-muted-foreground font-body mb-4 line-clamp-3">
                  {instructor.bio}
                </p>

                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div className="flex items-center space-x-2">
                    <Icon name="BriefcaseIcon" size={16} variant="outline" className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">
                      {instructor.experience}
                    </span>
                  </div>
                  <a
                    href={instructor.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:text-accent transition-colors duration-300"
                  >
                    <Icon name="LinkIcon" size={20} variant="outline" />
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}